/**
 * Copyright IBM Corp. 2020, 2025
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { defineProperty as _defineProperty, extends as _extends, objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default from 'react';
import PropTypes from '../../_virtual/index.js';
import { pkg } from '../../settings.js';
import cx from 'classnames';
import { Section, Button, Link } from '@carbon/react';

// The block part of our conventional BEM class names (blockClass__E--M).
var blockClass = "".concat(pkg.prefix, "--empty-state");
var componentName = 'EmptyStateContent';
var EmptyStateContent = function EmptyStateContent(_ref) {
  var action = _ref.action,
    link = _ref.link,
    headingAs = _ref.headingAs,
    size = _ref.size,
    subtitle = _ref.subtitle,
    title = _ref.title;
  return /*#__PURE__*/React__default.createElement("div", {
    className: "".concat(blockClass, "__content")
  }, /*#__PURE__*/React__default.createElement(Section, {
    as: headingAs,
    className: cx("".concat(blockClass, "__header"), _defineProperty({}, "".concat(blockClass, "__header--small"), size === 'sm'))
  }, title), subtitle && /*#__PURE__*/React__default.createElement("p", {
    className: cx("".concat(blockClass, "__subtitle"), _defineProperty({}, "".concat(blockClass, "__subtitle--small"), size === 'sm'))
  }, subtitle), (action === null || action === void 0 ? void 0 : action.text) && (action === null || action === void 0 ? void 0 : action.onClick) && /*#__PURE__*/React__default.createElement(Button, _extends({}, action, {
    className: "".concat(blockClass, "__action-button"),
    kind: action.kind || 'tertiary',
    onClick: action.onClick,
    renderIcon: action.renderIcon || null,
    size: 'sm'
  }), action.text), (link === null || link === void 0 ? void 0 : link.text) && (link === null || link === void 0 ? void 0 : link.href) && /*#__PURE__*/React__default.createElement(Link, _extends({}, link, {
    className: "".concat(blockClass, "__link"),
    href: link.href
  }), link.text));
};

// The display name of the component, used by React. Note that displayName
// is used in preference to relying on function.name.
EmptyStateContent.displayName = componentName;

// The types and DocGen commentary for the component props,
// in alphabetical order (for consistency).
// See https://www.npmjs.com/package/prop-types#usage.
EmptyStateContent.propTypes = {
  /**
   * Empty state action button
   */
  action: PropTypes.shape(_objectSpread2(_objectSpread2({}, Button.propTypes), {}, {
    iconDescription: PropTypes.string,
    kind: PropTypes.oneOf(['primary', 'secondary', 'tertiary']),
    renderIcon: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    onClick: Button.propTypes.onClick,
    text: PropTypes.string
  })),
  /**
   * Empty state headingAs allows you to customize the type of heading element
   */
  headingAs: PropTypes.elementType,
  /**
   * Empty state link object
   */
  link: PropTypes.shape(_objectSpread2(_objectSpread2({}, Link.propTypes), {}, {
    text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    href: PropTypes.string
  })),
  /**
   * Empty state size
   */
  size: PropTypes.oneOf(['lg', 'sm']),
  /**
   * Empty state subtitle
   */
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * Empty state title
   */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired
};

export { EmptyStateContent };
